import {
  AutoLinkNode,
  LinkNode,
  type AutoLinkAttributes,
  type LinkAttributes,
  type SerializedAutoLinkNode,
  type SerializedLinkNode,
} from "@payloadcms/richtext-lexical/lexical/link";
import {
  $addUpdateTag,
  $applyNodeReplacement,
  $createTextNode,
  $isElementNode,
  $parseSerializedNode,
  HISTORY_MERGE_TAG,
  RootNode,
  TabNode,
  type LexicalEditor,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
} from "@payloadcms/richtext-lexical/lexical";
import { mergeRegister } from "@payloadcms/richtext-lexical/lexical/utils";
import { DecoratorBlockNode } from "@payloadcms/richtext-lexical/lexical/react/LexicalDecoratorBlockNode";
import { createContext, createElement, useContext } from "react";
import type { ElementFormatType } from "@payloadcms/richtext-lexical/lexical";
import type { PageMedia } from "../../types";
import { pageContentSchemas } from "../../utils/validatePageContent";
import { isSafeFormFunnelContentHref } from "@portal/app/_common/lib/formFunnelContent";

type PayloadLinkFields = { linkType: "custom"; url: string; newTab?: boolean; doc?: null };
type PayloadLinkMetadata = { fields?: PayloadLinkFields; id?: string };
type SavedLink = SerializedLinkNode & PayloadLinkMetadata;
type SavedAutoLink = SerializedAutoLinkNode & PayloadLinkMetadata;

export const PageRichTextMediaContext = createContext<readonly PageMedia[]>([]);

interface SavedUpload extends SerializedLexicalNode {
  type: "upload";
  version: 3;
  format?: ElementFormatType;
  relationTo: "media";
  value: string;
  id?: string;
  fields?: { alt?: string } | null;
}

function UploadImage({ upload }: { upload: SavedUpload }) {
  const media = useContext(PageRichTextMediaContext);
  const image = media.find((item) => item.id === upload.value);
  const parsed = pageContentSchemas.image.safeParse({
    image: image && {
      id: image.id,
      url: image.url,
      alt: image.alt,
      width: image.width,
      height: image.height,
    },
  });
  if (!parsed.success) return createElement("span", null, "Image unavailable");
  return createElement("img", {
    src: parsed.data.image.url,
    alt: upload.fields?.alt ?? parsed.data.image.alt,
    width: parsed.data.image.width,
    height: parsed.data.image.height,
    style: { display: "block", maxWidth: "100%", height: "auto" },
  });
}

/** Payload's upload UI needs Payload editor providers; page edits preserve its native JSON instead. */
export class PageUploadNode extends DecoratorBlockNode {
  __upload: SavedUpload;

  static getType() {
    return "upload";
  }

  static clone(node: PageUploadNode) {
    return new PageUploadNode(node.__upload, node.__key);
  }

  constructor(
    upload: SavedUpload = { type: "upload", version: 3, relationTo: "media", value: "" },
    key?: NodeKey
  ) {
    super(upload.format ?? "", key);
    this.__upload = upload;
  }

  static importJSON(upload: SerializedLexicalNode & Record<string, unknown>) {
    if (
      !pageContentSchemas.richText.safeParse({
        content: { root: { type: "root", children: [upload] } },
      }).success
    ) {
      throw new Error("Unsupported page upload reference.");
    }
    return $applyNodeReplacement(new PageUploadNode(upload as unknown as SavedUpload));
  }

  exportJSON(): SavedUpload & { format: ElementFormatType } {
    return { ...this.__upload, format: this.getFormat() };
  }

  decorate() {
    return createElement(UploadImage, { upload: this.__upload });
  }
}

const attributes = (node: SavedLink): LinkAttributes => ({
  target: node.fields ? (node.fields.newTab ? "_blank" : null) : node.target,
  rel: node.fields?.newTab ? "noopener noreferrer" : node.rel,
});

/** Keep Payload's fields and editorial IDs while using Lexical's native link behavior. */
export class PageLinkNode extends LinkNode {
  __payloadId?: string;
  __payloadDoc?: null;

  static getType() {
    return "page-link";
  }

  static clone(node: PageLinkNode) {
    const clone = new PageLinkNode(node.__url, {}, node.__key);
    clone.__payloadId = node.__payloadId;
    clone.__payloadDoc = node.__payloadDoc;
    return clone;
  }

  constructor(url = "", linkAttributes: LinkAttributes = {}, key?: NodeKey) {
    super(url, linkAttributes, key);
  }

  static importJSON(node: SavedLink) {
    const imported = $applyNodeReplacement(new PageLinkNode()).updateFromJSON({
      ...node,
      url: node.fields?.url ?? node.url,
      ...attributes(node),
    });
    imported.__payloadId = node.id;
    imported.__payloadDoc = node.fields?.doc;
    return imported;
  }

  exportJSON(): SavedLink {
    return {
      ...super.exportJSON(),
      fields: {
        linkType: "custom",
        url: this.getURL(),
        newTab: this.getTarget() === "_blank",
        ...(this.__payloadDoc === null && { doc: null }),
      },
      ...(this.__payloadId && { id: this.__payloadId }),
    };
  }

  sanitizeUrl(url: string) {
    return isSafeFormFunnelContentHref(url) ? url : "about:blank";
  }
}

export class PageAutoLinkNode extends AutoLinkNode {
  __payloadId?: string;
  __payloadDoc?: null;

  static getType() {
    return "page-autolink";
  }

  static clone(node: PageAutoLinkNode) {
    const clone = new PageAutoLinkNode(node.__url, {}, node.__key);
    clone.__payloadId = node.__payloadId;
    clone.__payloadDoc = node.__payloadDoc;
    return clone;
  }

  constructor(url = "", linkAttributes: AutoLinkAttributes = {}, key?: NodeKey) {
    super(url, linkAttributes, key);
  }

  static importJSON(node: SavedAutoLink) {
    const imported = $applyNodeReplacement(new PageAutoLinkNode()).updateFromJSON({
      ...node,
      url: node.fields?.url ?? node.url,
      ...attributes(node),
    });
    imported.__payloadId = node.id;
    imported.__payloadDoc = node.fields?.doc;
    return imported;
  }

  exportJSON(): SavedAutoLink {
    return {
      ...super.exportJSON(),
      fields: {
        linkType: "custom",
        url: this.getURL(),
        newTab: this.getTarget() === "_blank",
        ...(this.__payloadDoc === null && { doc: null }),
      },
      ...(this.__payloadId && { id: this.__payloadId }),
    };
  }

  sanitizeUrl(url: string) {
    return isSafeFormFunnelContentHref(url) ? url : "about:blank";
  }
}

/** Strip native LinkNode attributes: Payload renders links from the fields object. */
export function payloadRichTextState(value: unknown): Record<string, unknown> {
  function visit(input: unknown): unknown {
    if (Array.isArray(input)) return input.map(visit);
    if (!input || typeof input !== "object") return input;
    const node = Object.fromEntries(
      Object.entries(input).map(([key, child]) => [key, visit(child)])
    );
    if (node.type === "page-link" || node.type === "page-autolink") {
      node.type = node.type === "page-link" ? "link" : "autolink";
      for (const key of ["url", "target", "rel", "title"]) delete node[key];
    }
    return node;
  }
  return visit(value) as Record<string, unknown>;
}

/** Use distinct editor node types so native link commands replace nodes correctly. */
export function lexicalRichTextState(value: Record<string, unknown>): string {
  function visit(input: unknown): unknown {
    if (Array.isArray(input)) return input.map(visit);
    if (!input || typeof input !== "object") return input;
    const node = Object.fromEntries(
      Object.entries(input).map(([key, child]) => [key, visit(child)])
    );
    if (node.type === "link") node.type = "page-link";
    if (node.type === "autolink") node.type = "page-autolink";
    return node;
  }
  return JSON.stringify(visit(value));
}

export const pageLinkNodes = [
  PageLinkNode,
  PageAutoLinkNode,
  {
    replace: LinkNode,
    with: (node: LinkNode) =>
      new PageLinkNode(node.getURL(), {
        target: node.getTarget(),
        rel: node.getRel(),
        title: node.getTitle(),
      }),
    withKlass: PageLinkNode,
  },
  {
    replace: AutoLinkNode,
    with: (node: AutoLinkNode) =>
      new PageAutoLinkNode(node.getURL(), {
        target: node.getTarget(),
        rel: node.getRel(),
        isUnlinked: node.getIsUnlinked(),
      }),
    withKlass: PageAutoLinkNode,
  },
];

type SerializedTree = SerializedLexicalNode & { children?: SerializedTree[] };
function serializedTree(node: LexicalNode): SerializedTree {
  return {
    ...node.exportJSON(),
    ...($isElementNode(node) ? { children: node.getChildren().map(serializedTree) } : {}),
  };
}

/** Normalize clipboard nodes before reconciliation/history, keeping Puck's document valid. */
export function registerRichTextNormalization(
  editor: LexicalEditor,
  getLastValid: () => Record<string, unknown>,
  onRejected: () => void
) {
  const unwrapUnsupportedLink = (node: PageLinkNode | PageAutoLinkNode) => {
    if (node.getURL().length <= 2048 && isSafeFormFunnelContentHref(node.getURL())) return;
    for (const child of node.getChildren()) node.insertBefore(child);
    node.remove();
  };
  return mergeRegister(
    editor.registerNodeTransform(TabNode, (node) => {
      node.replace($createTextNode(" ").setFormat(node.getFormat()));
    }),
    editor.registerNodeTransform(PageLinkNode, unwrapUnsupportedLink),
    editor.registerNodeTransform(PageAutoLinkNode, unwrapUnsupportedLink),
    editor.registerNodeTransform(RootNode, (root) => {
      const next = payloadRichTextState({ root: serializedTree(root) });
      if (pageContentSchemas.richText.safeParse({ content: next }).success) return;
      const previous = JSON.parse(lexicalRichTextState(getLastValid())) as {
        root: { children: SerializedLexicalNode[] };
      };
      root.clear().append(...previous.root.children.map((node) => $parseSerializedNode(node)));
      root.selectEnd();
      // Rejected input is not a new undo step and never reaches Puck.
      $addUpdateTag(HISTORY_MERGE_TAG);
      onRejected();
    })
  );
}
